import { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import KpiCard from "../components/KpiCard/KpiCard";
import BarChart from "../components/BarChart/BarChart";
import LineChart from "../components/LineChart/LineChart";
import DataTable, { type DataColumn } from "../components/DataTable/DataTable";
import EmptyState from "../components/EmptyState/EmptyState";
import useTrackingAggregate from "../hooks/useTrackingAggregate";
import useTrackingFunnel from "../hooks/useTrackingFunnel";
import { useSingleSurfaceResolver } from "../hooks/useEntityResolver";
import { adminRouteForSurface } from "../utils/surfaceAdminRoutes";
import { clickEvent, EventNames, impressionEvent } from "../utils/eventNames";
import { autoBucketFor, formatRange, type Period } from "../utils/period";
import { slugForFilename } from "../utils/csv";

interface AnnouncementRow {
	/**
	 * The user-visible row key. After title resolution this is the
	 * announcement id (so impressions roll up per announcement);
	 * before resolution it falls back to the raw entity_id (Guid).
	 */
	row_id: string;
	/** Resolved announcement title. Falls back to a truncated UUID. */
	title: string;
	/** Resolved announcement primary key, used to link to its admin page. */
	announcement_id: number | null;
	impressions: number;
	clicks: number;
	ctr: number;
}

const truncateGuid = (id: string) =>
	id.length > 12 ? `${id.slice(0, 8)}…` : id;

interface Props {
	period: Period;
	availableEvents: Set<string>;
}

const sumCount = (buckets?: { count: number }[] | null): number | null =>
	buckets ? buckets.reduce((acc, b) => acc + b.count, 0) : null;

const SocialTab: FC<Props> = ({ period, availableEvents }) => {
	const navigate = useNavigate();
	const seenEnabled = availableEvents.has(EventNames.SOCIAL_POST_SEEN);
	const reactedEnabled = availableEvents.has(EventNames.SOCIAL_POST_REACTED);
	const videoEnabled = availableEvents.has(EventNames.VIDEO_PLAYED);

	const seen = useTrackingAggregate(
		{
			event: EventNames.SOCIAL_POST_SEEN,
			from: period.from,
			to: period.to,
			groupBy: "day"
		},
		{ enabled: seenEnabled }
	);
	const reacted = useTrackingAggregate(
		{
			event: EventNames.SOCIAL_POST_REACTED,
			from: period.from,
			to: period.to,
			groupBy: "day"
		},
		{ enabled: reactedEnabled }
	);
	const videos = useTrackingAggregate(
		{
			event: EventNames.VIDEO_PLAYED,
			from: period.from,
			to: period.to,
			groupBy: "day"
		},
		{ enabled: videoEnabled }
	);

	const hashtags = useTrackingAggregate(
		{
			event: EventNames.SOCIAL_POST_SEEN,
			from: period.from,
			to: period.to,
			groupBy: "property:hashtag"
		},
		{ enabled: seenEnabled }
	);

	const annImpEvent = impressionEvent("ANNOUNCEMENT");
	const annClkEvent = clickEvent("ANNOUNCEMENT");
	const annImpAvail = availableEvents.has(annImpEvent);
	const annClkAvail = availableEvents.has(annClkEvent);

	const annImpressions = useTrackingAggregate(
		{
			event: annImpEvent,
			from: period.from,
			to: period.to,
			groupBy: "entityId"
		},
		{ enabled: annImpAvail }
	);
	const annClicks = useTrackingAggregate(
		{
			event: annClkEvent,
			from: period.from,
			to: period.to,
			groupBy: "entityId"
		},
		{ enabled: annClkAvail }
	);
	const annTrend = useTrackingAggregate(
		{
			event: annImpEvent,
			from: period.from,
			to: period.to,
			groupBy: autoBucketFor(period)
		},
		{ enabled: annImpAvail }
	);

	// Collect every entity_id (notification Guid) the tracking API
	// returned across both events so we can resolve them to titles.
	// Drop the synthetic `(null)` bucket the backend emits for events
	// missing an entity_id (and any empty string), so we don't waste
	// a round-trip on values the resolver can never match.
	const annEntityIds = useMemo(() => {
		const ids = new Set<string>();
		(annImpressions.data?.buckets ?? []).forEach((b) => ids.add(b.bucket));
		(annClicks.data?.buckets ?? []).forEach((b) => ids.add(b.bucket));
		return Array.from(ids).filter((id) => id && id !== "(null)");
	}, [annImpressions.data, annClicks.data]);

	const annLookup = useSingleSurfaceResolver("ANNOUNCEMENT", annEntityIds, {
		enabled: annImpAvail || annClkAvail
	});

	const annRows: AnnouncementRow[] = useMemo(() => {
		// Aggregate by announcementId so each announcement is a SINGLE
		// row in the dashboard regardless of how many recipients saw
		// or clicked it. Notifications that don't resolve (deleted
		// announcements, in-flight lookup) keep the entity_id as the
		// row key so we don't silently merge unrelated rows.
		const map = new Map<string, AnnouncementRow>();
		const upsert = (entityId: string, kind: "imp" | "clk", count: number) => {
			const resolved = annLookup.lookup[entityId];
			const rowId = resolved ? `announcement:${resolved.entity_id}` : entityId;
			const title = resolved?.title ?? truncateGuid(entityId);
			const row = map.get(rowId) ?? {
				row_id: rowId,
				title,
				announcement_id: resolved?.entity_id ?? null,
				impressions: 0,
				clicks: 0,
				ctr: 0
			};
			if (kind === "imp") row.impressions += count;
			else row.clicks += count;
			map.set(rowId, row);
		};

		(annImpressions.data?.buckets ?? []).forEach((b) =>
			upsert(b.bucket, "imp", b.count)
		);
		(annClicks.data?.buckets ?? []).forEach((b) =>
			upsert(b.bucket, "clk", b.count)
		);

		const out = Array.from(map.values());
		for (const r of out) {
			r.ctr = r.impressions > 0 ? r.clicks / r.impressions : 0;
		}
		out.sort((a, b) => b.impressions - a.impressions);
		return out;
	}, [annImpressions.data, annClicks.data, annLookup.lookup]);

	const annColumns: DataColumn<AnnouncementRow>[] = [
		{
			key: "title",
			header: "Announcement",
			render: (r) => r.title || "(none)",
			csv: (r) => r.title
		},
		{
			key: "impressions",
			header: "Impressions",
			align: "right",
			render: (r) => r.impressions.toLocaleString(),
			csv: (r) => r.impressions
		},
		{
			key: "clicks",
			header: "Clicks",
			align: "right",
			render: (r) => r.clicks.toLocaleString(),
			csv: (r) => r.clicks
		},
		{
			key: "ctr",
			header: "CTR",
			align: "right",
			render: (r) => `${(r.ctr * 100).toFixed(1)}%`,
			csv: (r) => (r.ctr * 100).toFixed(2)
		}
	];

	const funnelSteps = [
		EventNames.NOTIFICATION_DELIVERED,
		EventNames.NOTIFICATION_OPENED,
		EventNames.ANNOUNCEMENT_READ
	];
	const funnelEnabled = funnelSteps.every((s) => availableEvents.has(s));
	const funnel = useTrackingFunnel(
		{
			steps: funnelSteps,
			from: period.from,
			to: period.to,
			withinSeconds: 300
		},
		{ enabled: funnelEnabled }
	);

	return (
		<div className="space-y-8">
			<div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
				<KpiCard
					label="Posts seen"
					loading={seen.isLoading}
					error={seen.isError}
					value={
						seenEnabled
							? (sumCount(seen.data?.buckets)?.toLocaleString() ?? null)
							: null
					}
					emptyText={!seenEnabled ? "Event not emitted yet" : undefined}
				/>
				<KpiCard
					label="Reactions"
					loading={reacted.isLoading}
					value={
						reactedEnabled
							? (sumCount(reacted.data?.buckets)?.toLocaleString() ?? null)
							: null
					}
					emptyText={!reactedEnabled ? "Event not emitted yet" : undefined}
				/>
				<KpiCard
					label="Videos played"
					loading={videos.isLoading}
					value={
						videoEnabled
							? (sumCount(videos.data?.buckets)?.toLocaleString() ?? null)
							: null
					}
					emptyText={!videoEnabled ? "Event not emitted yet" : undefined}
				/>
			</div>

			{(() => {
				// Drop buckets with no hashtag value. The aggregate
				// returns one row per distinct property value; when
				// SOCIAL_POST_SEEN events are emitted without a
				// `hashtag` property the backend's
				// `BucketByPropertyAsync` materialises the absence as
				// the literal string `"(null)"` (see TrackingService),
				// not a JSON null — so we filter that exact value
				// alongside empty / non-string defenses. Rendering it
				// would surface a meaningless `#(null)` bar with the
				// entire event volume.
				const allBuckets = hashtags.data?.buckets ?? [];
				const taggedBuckets = allBuckets.filter(
					(b) =>
						typeof b.bucket === "string" &&
						b.bucket.trim() !== "" &&
						b.bucket !== "(null)"
				);
				// Distinguish "no events at all" from "events landing
				// but none carry a hashtag property". The latter is
				// the case where every event collapses into the
				// `(null)` bucket — that's the only state where the
				// "attach hashtag to event" diagnosis is correct.
				const hasAnyEvents = allBuckets.length > 0;
				const hasUntaggedEvents = allBuckets.some(
					(b) => b.bucket === "(null)" || b.bucket === ""
				);
				const eventsLackHashtagProperty =
					hasAnyEvents && taggedBuckets.length === 0 && hasUntaggedEvents;
				return (
					<div className="rounded-xl border border-border bg-white shadow-sm p-4">
						<div className="flex items-baseline justify-between mb-3 gap-2">
							<h3 className="font-heading text-base font-medium">
								Top hashtags
							</h3>
							{seenEnabled &&
							!hashtags.isLoading &&
							taggedBuckets.length > 0 ? (
								<p className="text-xs text-muted-foreground">
									Showing top{" "}
									{Math.min(10, taggedBuckets.length).toLocaleString()} of{" "}
									{taggedBuckets.length.toLocaleString()}
								</p>
							) : null}
						</div>
						{!seenEnabled ? (
							<EmptyState
								title="No data yet"
								description="SOCIAL_POST_SEEN is not being emitted yet."
							/>
						) : hashtags.isLoading ? (
							<div className="h-32 animate-pulse bg-zinc-100 rounded" />
						) : eventsLackHashtagProperty ? (
							<EmptyState
								title="No hashtag data yet"
								description="SOCIAL_POST_SEEN events are landing, but they don't carry a `hashtag` property — the apps need to attach the post's hashtag to the event before this chart can populate."
								tone="warn"
							/>
						) : taggedBuckets.length === 0 ? (
							<EmptyState
								title="No data for this range"
								description="No SOCIAL_POST_SEEN events were recorded in the selected period. Try a longer date range."
							/>
						) : (
							<BarChart
								ariaLabel="Top hashtags by appearances"
								data={taggedBuckets.slice(0, 10).map((b) => ({
									label: `#${b.bucket}`,
									value: b.count
								}))}
								valueLabel="appearances"
							/>
						)}
					</div>
				);
			})()}

			<div className="rounded-xl border border-border bg-white shadow-sm p-4 space-y-4">
				<h3 className="font-heading text-base font-medium">Announcements</h3>
				{!annImpAvail && !annClkAvail ? (
					<EmptyState
						title="Announcement events aren't being emitted yet"
						description={`Expected events: ${annImpEvent}, ${annClkEvent}.`}
						tone="warn"
					/>
				) : (
					<>
						<DataTable
							caption={`Announcements by impressions — ${period.label}`}
							rows={annRows}
							columns={annColumns}
							rowKey={(r) => r.row_id}
							onRowClick={(r) => {
								if (r.announcement_id === null) return;
								const route = adminRouteForSurface(
									"ANNOUNCEMENT",
									r.announcement_id
								);
								if (route) navigate(route);
							}}
							loading={
								annImpressions.isLoading ||
								annClicks.isLoading ||
								annLookup.isLoading
							}
							csvFilename={`announcements-${slugForFilename(
								formatRange(period.from, period.to)
							)}.csv`}
							pagination={{ pageSize: 10, itemLabel: "announcements" }}
						/>
						<div>
							<h4 className="font-heading text-sm font-medium mb-3">
								Announcement impressions over time
							</h4>
							{annTrend.isLoading ? (
								<div className="h-40 animate-pulse bg-zinc-100 rounded" />
							) : (
								<LineChart
									ariaLabel="Announcement impressions over time"
									yLabel="Impressions"
									series={[
										{
											label: "Impressions",
											color: "var(--color-primary)",
											data: (annTrend.data?.buckets ?? []).map((b) => ({
												x: b.bucket,
												y: b.count
											}))
										}
									]}
								/>
							)}
						</div>
					</>
				)}
			</div>

			<div className="rounded-xl border border-border bg-white shadow-sm p-4">
				<h3 className="font-heading text-base font-medium mb-3">
					Notification funnel (within 5 minutes)
				</h3>
				{!funnelEnabled ? (
					<EmptyState
						title="Funnel events incomplete"
						description={`Required steps: ${funnelSteps.join(" → ")}.`}
						tone="warn"
					/>
				) : funnel.isLoading ? (
					<div className="h-32 animate-pulse bg-zinc-100 rounded" />
				) : funnel.isError ? (
					<EmptyState
						title="Failed to load funnel"
						description="The backend rejected one of the steps. Check the event vocabulary."
						tone="warn"
					/>
				) : (
					<BarChart
						ariaLabel="Notification funnel users reached per step"
						data={(funnel.data?.steps ?? []).map((s) => ({
							label: `${s.index + 1}. ${s.step}`,
							value: s.users_reached
						}))}
						formatValue={(n) => n.toLocaleString()}
						valueLabel="users"
					/>
				)}
			</div>
		</div>
	);
};

export default SocialTab;
